import { useMemo } from "react";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import SubHeadings from "../../components/SubHeadings";
import { FormField, QueryItem } from "../../types/Query";

interface Props {
  fields: FormField[];
  formData: QueryItem;
  setFormData: React.Dispatch<React.SetStateAction<QueryItem>>;
  isEditing: boolean;
}

export default function OtherData({
  fields,
  formData,
  setFormData,
  isEditing,
}: Props) {
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getOptions = (field: FormField) =>
    field.options
      ?.flatMap((o) => o.replace(/[\[\]]/g, "").split(","))
      .map((o) => o.trim().replace(/^"(.*)"$/, "$1")) || [];

  const orderedFields = useMemo(() => {
    const FIELD_KEYS = {
      REMARKS: ["remarks", "additionalRemarks"],
    };
    const remarks = fields.filter((f) =>
      FIELD_KEYS.REMARKS.includes(f.fieldAbbr)
    );

    const rest = fields.filter(
      (f) => !FIELD_KEYS.REMARKS.includes(f.fieldAbbr)
    );

    return [...rest, ...remarks];
  }, [fields]);

  return (
    <div className="flex flex-col gap-7 w-full py-7 border-t border-neutral-50">
      <SubHeadings text="Other Details" />

      <form className="grid lg:grid-cols-2 gap-7">
        {orderedFields.map((field) => {
          const commonProps = {
            key: field.id,
            name: field.fieldAbbr,
            label: field.fieldName,
            readOnly: !isEditing,
            value: formData[field.fieldAbbr] || "",
            onChange: handleChange,
          };

          if (
            [
              "TEXT",
              "EMAIL",
              "PHONE",
              "NUMBER",
              "DATE",
              "SINGLE_LINE_ANSWER",
              "MULTI_LINE_ANSWER",
              "PHONE_MOBILE_NUMBER",
            ].includes(field.fieldType)
          ) {
            const isRemarks =
              field.fieldAbbr === "remarks" ||
              field.fieldAbbr === "additionalRemarks";
            return (
              <CustomInput
                {...commonProps}
                type={
                  field.fieldType === "EMAIL"
                    ? "email"
                    : field.fieldType === "PHONE"
                    ? "tel"
                    : field.fieldType === "DATE"
                    ? "date"
                    : field.fieldType === "NUMBER"
                    ? "number"
                    : "text"
                }
                placeholder={field.fieldDescription}
                inputClassName="w-full"
                isTextarea={isRemarks}
                rows={isRemarks ? 4 : undefined}
                className={isRemarks ? "lg:col-span-2" : ""}
              />
            );
          }

          /* DROPDOWN */
          if (
            field.fieldType === "DROPDOWN" ||
            field.fieldType === "YES_NO" ||
            field.fieldType === "SINGLE_SECLECTION_FROM_LIST_OF_OPTION" ||
            field.fieldType === "MULTI_SECLECTION_FROM_LIST_OF_OPTION"
          ) {
            const OPTION_COLOR_CLASSES = [
              "text-success-600",
              "text-primary-500",
              "text-warning-500",
            ];

            const getOptionClassName = (field: FormField) => {
              const hash = [...field.fieldAbbr].reduce(
                (acc, char) => acc + char.charCodeAt(0),
                0
              );

              return (
                OPTION_COLOR_CLASSES[hash % OPTION_COLOR_CLASSES.length] +
                " font-semibold"
              );
            };

            return (
              <CustomSelect
                {...commonProps}
                options={getOptions(field)}
                placeholder={`Select ${field.fieldName}`}
                optionClassName={getOptionClassName(field)}
              />
            );
          }

          return null;
        })}
      </form>
    </div>
  );
}
